import {selx} from 'clsx';
import isEqual from 'lodash/isEqual';
import {jsx} from 'panel';
import last from 'lodash/last';

import {QueryContext} from 'common/query-context';
import {
  ChartType,
  PRIMITIVE_MATH_TYPES,
  ProjectFeatureFlag,
  REPORT_BASICS_LINKS,
  ReportName,
  SHOW_LAST_X_DAYS_FEATURE_FLAGS,
} from 'common/report/constants';
import {FilterSection, Section} from 'common/report/insights/models/section';
// TODO move common/report/util/insights to common/report/insights/util
import {QueryMode} from 'common/report/insights/util/constants';
import {getEntries, getShowSection} from 'common/report/insights/util/events';
import {getFilterSection, getPropertyFilters, getPropertyFiltersOperator} from 'common/report/insights/util/filters';
import {getSegmentSections, getSelectedSegments} from 'common/report/insights/util/segments';
import {getDateRange, getTimeSection} from 'common/report/insights/util/time';
import {
  _getDefaultInsightsSorting,
  getNumMultiAttributionProps,
  getQueryModeHeaderTitle,
  getQueryModeRowTypes,
  getQueryTypeOptions,
  getSortConfig,
  hasOnlyEventMetrics,
  hasValidMetricBookmarkParams,
} from 'common/report/insights/util';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {isEventFilter, isUserFilter} from 'common/widgets/property-filter-menu/models/utils';
import {QueryBuilderSelection, QueryBuilderUpdateDetail} from 'common/query-builder/query-builder/types';
import {
  isEventEntry,
  QueryEventEntry,
  QueryMetricEntry,
  QuerySuperEntry,
} from 'common/query-builder/query-entry-section/types';
import {InsightsFormulaEl} from 'common/report/insights/query-builder/insights-formula';
import {ReportQueryBuilder} from 'common/report/report-manager/query-builder';
import {
  ChartPlotStyle,
  InsightsBookmarkParams,
  InsightsQueryError,
  InsightsQueryResults,
} from 'common/report/insights/util/types';
import {emitTrackEvent} from 'common/util/dom';
import {SectionEl} from 'common/components/section';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {LiftType} from 'common/report/util/lift/types';
import {setQueryDataGroup} from 'common/query-builder/query-builder/util/data-groups';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {getFirstSegment, isCohortSegment} from 'common/query-builder/query-block/segment-menu/types';
import {QueryReason} from 'common/report/util/query-tracking/types';
import styles from 'common/report/report-manager/query-builder.module.styl';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {QueryContextFnParams} from 'common/report/report-manager/types';
import {canUseViewN, isPivotTableEnabled} from 'common/components/data-table/utils';
import {
  BEHAVIOR_AGGREGATION,
  BEHAVIOR_FREQUENCY,
  BEHAVIOR_MULTI_ATTRIBUTION,
} from 'common/query-builder/query-block/properties-menu/constants';
import {
  isBehaviorSegmentation,
  isMultiAttributionSegmentation,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {canUseMultiAttribution} from 'common/widgets/multi-attribution-menu/utils';
import {DefaultMultiAttribution} from 'common/widgets/multi-attribution-menu/constants';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {addToast} from 'common/components/toast-area/toast-manager';
import {hasInlineBehaviors} from 'common/query-builder/query-builder/util';

import {INSIGHTS_DEFAULT_EVENT, Report} from '../models/report';
import {compareTimeUnits, XAU_TO_TIME_UNIT} from '../util';
import {Formula, formulaClauseToFormula, formulaToFormulaClause} from './insights-formula/types';
import {
  getFormulaDisabledReason,
  updateReportFormulaLabels,
  updateReportFormulaSection,
} from './insights-formula/utils';

export function getReportQueryContext(
  report: Report,
  {
    defaultReportState,
    originDashboard,
    dataGroups,
  }: {defaultReportState: Report; originDashboard: DashboardOrigin; dataGroups: DataGroup[]},
) {
  if (isEqual(defaultReportState.toBookmarkData(), report.toBookmarkData())) {
    return null;
  }

  const dateRange = getDateRange(report.sections.time.clauses).getAttributes();
  const dataGroupId = report.sections.getDataGroupId();

  const events: QueryEventEntry[] = [];
  const entries = getEntries(report.sections.show.clauses, dataGroups);
  entries.forEach(function (entry) {
    if (isEventEntry(entry)) {
      const insightsOnlyEventValues = [`$top_events`, `$all_events`];
      if (!insightsOnlyEventValues.includes(entry.event.value)) {
        events.push({
          type: entry.type,
          event: entry.event,
          filters: entry.filters.filter((f) => (isEventFilter(f) || isUserFilter(f)) && !isBehaviorSegmentation(f)),
          filtersOperator: entry.filtersOperator,
          ...(entry.multiAttribution ? {multiAttribution: entry.multiAttribution} : {}),
        });
      }
    }
  });

  const comparedDateRange = report.timeComparison;
  const segments = getSelectedSegments(report.sections.cohorts.clauses, report.sections.group.clauses);
  const filters = getPropertyFilters(report.sections.filter.clauses);

  return {
    bookmarkType: BookmarkType.Insights,
    querySamplingEnabled: report.querySamplingEnabled,
    dataGroupId,
    dateRange,
    ...(events.length && {events}),
    comparedDateRange,
    ...(originDashboard && {originDashboard}),
    segments: segments.filter((segment) => !isBehaviorSegmentation(getFirstSegment(segment))),
    filters: filters.filter((filter) => !isBehaviorSegmentation(filter)),
  };
}

export function getReportFromQueryContext(queryContext: QueryContext, params: QueryContextFnParams) {
  const report = Report.createDefaultReport();

  if (queryContext.dateRange) {
    report.sections.time = getTimeSection(queryContext.dateRange, {useDefaultTimeUnit: true});
  }

  if (queryContext.events) {
    report.sections = report.sections.replaceSection(getShowSection(queryContext.events, queryContext.dataGroupId));
  }

  report.sections.setDataGroupId({dataGroupId: queryContext.dataGroupId, customEvents: params.customEvents});

  if (queryContext.comparedDateRange) {
    report.timeComparison = queryContext.comparedDateRange;
  }

  if (queryContext.segments) {
    const segments = [];
    // TODO query cohorts like a property
    let hasCohortSegment = false;
    queryContext.segments.forEach((segment) => {
      if (isCohortSegment(segment)) {
        if (hasCohortSegment === false) {
          segments.push(segment);
        }
        hasCohortSegment = true;
      } else {
        segments.push(segment);
      }
    });

    getSegmentSections(segments).forEach((section) => {
      report.sections = report.sections.replaceSection(section);
    });
  }

  if (queryContext.filters) {
    report.sections = report.sections.replaceSection(getFilterSection(queryContext.filters, null));
  }

  if (queryContext.querySamplingEnabled) {
    report.querySamplingEnabled = true;
  }

  const numSegmentColumns = (queryContext.segments?.length || 0) + (queryContext.events?.length > 1 ? 1 : 0);
  report.sorting = _getDefaultInsightsSorting({
    numSegmentColumns,
    canUseViewN: canUseViewN(params.project),
    hasPivotTablesEnabled: isPivotTableEnabled(params.project),
  });

  return report;
}

/**
 * Makes sure bookmark sorting state matches the expected sorting state at all times.
 */
function updateReportSorting({
  report,
  hasPivotTablesEnabled,
  canUseViewN,
}: {
  report: Report;
  hasPivotTablesEnabled?: boolean;
  canUseViewN?: boolean;
}) {
  const updatedReport = report.clone();
  const {sections} = updatedReport;
  // Cohort clauses are always grouped into the same header and always come first after the `show` header
  const hasCohorts = sections.cohorts.clauses.length;
  const hasDistributionAggregation = sections.show.clauses.some((c) => c.math === PRIMITIVE_MATH_TYPES.HISTOGRAM);
  const headerCount = 1 + (hasCohorts ? 1 : 0) + sections.group.clauses.length + (hasDistributionAggregation ? 1 : 0);
  updatedReport.sorting = getSortConfig({
    headerCount,
    bookmarkSortConfig: updatedReport.sorting,
    hasPivotTablesEnabled,
    numMetrics: sections.formula.clauses.length || sections.show.clauses.length,
    canUseViewN,
    useDefaultViewNForNewSegments: true,
  });
  return updatedReport;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function updateReportFromQBSelection({
  selection,
  report,
  customEvents,
  hasPivotTablesEnabled = false,
  canUseViewN,
}: {
  selection: QueryBuilderUpdateDetail;
  report: Report;
  customEvents: CustomEventDefinition[];
  hasPivotTablesEnabled?: boolean;
  canUseViewN?: boolean;
}): Report {
  const oldSections = report.sections;
  const oldHasValidMetric = hasValidMetricBookmarkParams(report.toBookmarkData());

  report = report.clone();

  // for default data group, qb returns an empty string
  if (selection.dataGroupId !== (report.sections.getDataGroupId() ?? ``)) {
    report.sections.setDataGroupId({dataGroupId: selection.dataGroupId, customEvents});
  }

  const updatedSections: Section[] = [
    getShowSection(selection.entries as QueryMetricEntry[], selection.dataGroupId),
    // @ts-ignore - FilterSection.toUrlData seems incompatible with Section.toUrlData
    getFilterSection(selection.filters, selection.filtersOperator),
    ...getSegmentSections(selection.groupBy),
  ];

  updatedSections.forEach((updatedSection) => {
    report.sections = report.sections.replaceSection(updatedSection);
  });

  const cohortsChanged = !isEqual(report.sections.cohorts.clauses, oldSections.cohorts.clauses);
  const groupsChanged = !isEqual(report.sections.group.clauses, oldSections.group.clauses);
  const segmentsChanged = cohortsChanged || groupsChanged;
  const newHasValidMetric = hasValidMetricBookmarkParams(report.toBookmarkData());
  const addedMetric =
    // When loading Insights, it initially starts with 1 show clause that is incomplete, so when this incomplete metric
    // becomes valid, a metric has been added
    (!oldHasValidMetric && newHasValidMetric) || report.sections.show.clauses.length > oldSections.show.clauses.length;

  const newNumMultiAttributionProps = getNumMultiAttributionProps(report.sections.group.clauses);
  const oldNumMultiAttributionProps = getNumMultiAttributionProps(oldSections.group.clauses);

  // Case when first attribution property is added
  if (oldNumMultiAttributionProps === 0 && newNumMultiAttributionProps > 0 && newHasValidMetric) {
    // Add attribution model to all metrics when first attribution property is added
    report.sections.show.clauses.forEach((clause) => {
      clause.multiAttribution = {...DefaultMultiAttribution};
    });
  }
  // Case when the last attribution property is removed
  else if (oldNumMultiAttributionProps > 0 && newNumMultiAttributionProps === 0) {
    // Remove attribution models from metrics when all attribution property are removed
    report.sections.show.clauses.forEach((clause) => {
      delete clause.multiAttribution;
    });
  }

  if (newNumMultiAttributionProps > 0 && addedMetric) {
    const newShowClause: any = last(report.sections.show.clauses);
    // The added metric might already have multi attribution model (if duplicating existing metric that had multi
    // attribution model).
    if (!newShowClause.multiAttribution) {
      newShowClause.multiAttribution = {...DefaultMultiAttribution};
    }
  }

  // disallowed chart types
  const {chartType, plotStyle} = report.displayOptions;
  switch (chartType) {
    case ChartType.Line: {
      const isPeopleAndNotTimeSeries =
        report.sections.show.isPeopleOnlyQuery() && !report.sections.group.isPeopleTimeSeries();
      if (isPeopleAndNotTimeSeries) {
        // do not allow line charts for non-timeseries people queries
        report.displayOptions.chartType = ChartType.Bar;
      }
      break;
    }
    case ChartType.Bar: {
      const isCohortClauseWithoutGroups = report.sections.cohorts.hasClauses() && !report.sections.group.hasClauses();
      if (isCohortClauseWithoutGroups && plotStyle === ChartPlotStyle.Stacked) {
        // do not allow stacked bar for queries with cohorts and no groups
        report.displayOptions.plotStyle = ChartPlotStyle.Standard;
      }
      break;
    }
  }

  // correct time unit if clause type has changed
  const timeClause = report.sections.time.clauses[0];
  if (report.isCohortsOverTime() && chartType !== ChartType.Line) {
    report.displayOptions.chartType = ChartType.Line;
  }

  // TODO why dont we use the same helper that the chart header component uses?
  if (
    (report.isCohortsOverTime() || report.hasBehavioralFilterOrSegmentation()) &&
    [`minute`, `hour`].includes(timeClause.unit)
  ) {
    timeClause.unit = `day`;
  }

  if (report.sections.show.hasXauClauses()) {
    if (chartType !== ChartType.Line) {
      // href to current location will redirect to the current pre-change clean URL
      addToast({
        message: `DAU/MAU/WAU calculations are only supported for line charts.`,
        ctaHref: window.location.href,
        cta: `Undo`,
      });
      report.displayOptions.chartType = ChartType.Line;
      report.displayOptions.plotStyle = ChartPlotStyle.Standard;
    }
    const maxTimeUnit = XAU_TO_TIME_UNIT[report.sections.show.lowestXauTimeUnit()];
    const minTimeUnit = TimeUnit.Day;
    const aboveMax = compareTimeUnits(maxTimeUnit, timeClause.unit) < 0;
    const belowMin = compareTimeUnits(minTimeUnit, timeClause.unit) > 0;
    if (aboveMax) {
      timeClause.unit = maxTimeUnit;
    } else if (belowMin) {
      timeClause.unit = minTimeUnit;
    }
  }

  // If any of the show clause has rolling or cumulative calculation, change chart type to line chart
  if (
    (report.sections.show.hasRollingClauses() || report.sections.show.hasCumulativeClauses()) &&
    chartType !== ChartType.Line
  ) {
    // href to current location will redirect to the current pre-change clean URL
    addToast({
      message: `Rolling or cumulative calculations are only supported for line charts.`,
      ctaHref: window.location.href,
      cta: `Undo`,
    });
    report.displayOptions.chartType = ChartType.Line;
    report.displayOptions.plotStyle = ChartPlotStyle.Standard;
  }

  const liftType = report.liftComparison?.type;
  // If we change breakdowns, we don't want to compare against a baseline value that might not exist, so clear lift
  // We also can't do overall, because there's no segments to compare to overall. Time comparison is fine.
  if ((liftType === LiftType.Segment || liftType === LiftType.Overall) && segmentsChanged) {
    report.liftComparison = null;
  }

  report = updateReportFormulaLabels(report);

  report = updateReportSorting({report, hasPivotTablesEnabled, canUseViewN});

  return report;
}

function updateReportFromFormulaUpdate({
  report,
  formulas,
  hasPivotTablesEnabled,
  canUseViewN,
}: {
  report: Report;
  formulas: Formula[];
  hasPivotTablesEnabled?: boolean;
  canUseViewN?: boolean;
}) {
  let updatedReport = updateReportFormulaSection({report, formulas});
  updatedReport = updateReportSorting({report: updatedReport, hasPivotTablesEnabled, canUseViewN});
  return updatedReport;
}

/**
 * Sanitizes a report for the given data group using the QueryBuilder #setQueryDataGroup util.
 */
export function sanitizeReportForDataGroup({
  report,
  dataGroupId,
  dataGroups,
  customEvents,
}: {
  report: Report;
  dataGroupId: string;
  dataGroups: DataGroup[];
  customEvents: CustomEventDefinition[];
}) {
  // convert Report to QueryBuilderSelection
  const query: QueryBuilderSelection = {
    dataGroupId: report.sections.getDataGroupId(),
    dateRange: getDateRange(report.sections.time.clauses).getAttributes(),
    entries: getEntries(report.sections.show.clauses, dataGroups),
    exclusionSteps: [],
    groupBy: getSelectedSegments(report.sections.cohorts.clauses, report.sections.group.clauses),
    filters: getPropertyFilters(report.sections.filter.clauses),
    filtersOperator: getPropertyFiltersOperator(report.sections.filter.determiner),
    attributionSelection: null,
  };

  // sanitize the QueryBuilderSelection
  const sanitizedQuery = setQueryDataGroup({dataGroupId, query, customEvents});

  // convert the new QueryBuilderSelection to Report
  const sanitizedSelection = {...sanitizedQuery, delta: {}, forceQuery: false};
  return updateReportFromQBSelection({
    selection: sanitizedSelection,
    report,
    customEvents,
  });
}

type InsightsQueryBuilderState = {
  queryMode: QueryMode;
  reports: {
    [QueryMode.Events]: Report | null;
    [QueryMode.Groups]: Report | null;
  };
};
class InsightsQueryBuilder extends ReportQueryBuilder<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryBuilderState
> {
  displayContext = SearchDisplayContext.Insights;
  reportType = ReportName.Insights;
  _error: Nullable<InsightsQueryError> = null;

  get defaultState() {
    return {
      // The Insights QB keeps around several unsaved report states so that
      // users can flip between different "query modes:" Events(&Cohorts) / People
      // modes and not lose their work. It's an unfortunate hunk of statefulness in
      // the QB which otherwise could depend solely on a bookmark and not need the
      // whole Insights model layer. A UX solution to this would be for "people" and
      // "events" query modes to be top-level analysis types that you could add to a
      // board, rather than adding an "Insights" and then using the QB to choose People
      // or Events/Cohorts.
      queryMode: QueryMode.Events,
      reports: {
        [QueryMode.Events]: null,
        [QueryMode.Groups]: null,
      },
    };
  }

  bookmarkUpdateFromQbUpdate(updateDetail: QueryBuilderUpdateDetail): InsightsBookmarkParams {
    const {delta} = updateDetail;
    let params: InsightsBookmarkParams;

    if (`queryType` in delta) {
      if (delta.queryType !== this.state.queryMode) {
        this.update({queryMode: delta.queryType as QueryMode});
      }

      params = this.report.toBookmarkData();
    } else {
      const report = updateReportFromQBSelection({
        selection: updateDetail,
        report: this.report,
        customEvents: this.params.customEvents,
        hasPivotTablesEnabled: isPivotTableEnabled(this.params.project),
        canUseViewN: canUseViewN(this.params.project),
      });

      params = report.toBookmarkData();
    }

    return params;
  }

  public connectedCallback() {
    super.connectedCallback();
    this.updateReports(this.params.bookmarkParams);
  }

  qbChildrenTemplate() {
    return (
      <div>
        <SectionEl
          sel={selx(styles.qbSection)}
          attrs={{
            'non-collapsible': true,
          }}
        >
          <InsightsFormulaEl
            attrs={{
              'project-id': this.params.projectId,
              'is-sidebar-qb': true,
              'disabled-reason': this.report ? getFormulaDisabledReason(this.report) : ``,
            }}
            props={{
              formulas: this.report?.sections.formula.clauses.map(formulaClauseToFormula) ?? [],
              error: this.error,
            }}
            on={{
              update: ({detail}) => {
                const {formulas, trackingInfo} = detail;
                const updatedReport = updateReportFromFormulaUpdate({
                  report: this.report,
                  formulas,
                  hasPivotTablesEnabled: isPivotTableEnabled(this.params.project),
                  canUseViewN: canUseViewN(this.params.project),
                });
                emitTrackEvent(this, {
                  eventName: trackingInfo.event,
                  props: formulaToFormulaClause(trackingInfo.formula).trackingData(),
                });
                this.updateReport(updatedReport);

                this.dispatchUpdate({params: updatedReport.toBookmarkData(), queryReason: QueryReason.QbOtherUpdate});
              },
            }}
          />
        </SectionEl>
      </div>
    );
  }

  set error(error: InsightsQueryError) {
    this._error = error;
    this.update();
  }

  get error() {
    return this._error;
  }

  get currentReport() {
    return this.state.reports[this.state.queryMode];
  }

  reportSpecificQBParams() {
    if (!this.currentReport) {
      return {};
    }

    const {displayOptions, sections} = this.currentReport;
    const entries = getEntries(sections.show.clauses, this.params.dataGroups);
    const dataGroupId = sections.getDataGroupId();
    const canUseMultiAttributionVal = canUseMultiAttribution(this.params.project);
    const hasMultiAttributionProp = getNumMultiAttributionProps(sections.group.clauses) > 0;

    const groupBy = getSelectedSegments(sections.cohorts.clauses, sections.group.clauses);
    const groupByUndraggableRows = groupBy.reduce((acc, segment, idx) => {
      if (isCohortSegment(segment) || isMultiAttributionSegmentation(getFirstSegment(segment))) {
        acc.push(idx);
      }
      return acc;
    }, [] as number[]);

    return {
      allowCohortFilter: true,
      addEntryButtonLabel: `Add Metric`,
      addEntryButtonTooltipText: `Add an event or cohort to analyze.\n[Read Intro to Insights](${
        REPORT_BASICS_LINKS[ReportName.Insights]
      })`,
      canCustomizeNumericBuckets: true,
      canUseMultiAttribution: canUseMultiAttributionVal && hasMultiAttributionProp,
      ...(dataGroupId ? {dataGroupId} : {}),
      dateRange: getDateRange(sections.time.clauses).getAttributes(),
      dateUnit: sections.time.clauses[0].unit,
      disableAddEntryReason:
        this.currentReport.displayOptions.chartType === ChartType.Table && this.currentReport.hasTopEvents()
          ? `Events cannot be added on tables with "Your Top Events".`
          : ``,
      enableEnhancedListFilters: true,
      entryRowLabelType: `alpha`,
      entriesDraggable: true,
      entries,
      minEntries: 1,
      emptyPlaceholder:
        this.state.queryMode === QueryMode.Events
          ? ({...INSIGHTS_DEFAULT_EVENT, dataGroupId} as QuerySuperEntry)
          : null,
      includeCustomProperties: `all`,
      filtersOperator: getPropertyFiltersOperator(sections.filter.determiner || FilterSection.DETERMINER_ALL),
      filters: getPropertyFilters(sections.filter.clauses),
      groupBy,
      groupByUndraggableRows,
      hideDatePicker: true,
      metricsMode: true,
      otherFilterOptions: this.canUseBehaviors() ? [BEHAVIOR_FREQUENCY.value, BEHAVIOR_AGGREGATION.value] : [],
      otherGroupByOptions: [
        ...(this.canUseBehaviors() ? [BEHAVIOR_FREQUENCY.value, BEHAVIOR_AGGREGATION.value] : []),
        ...(canUseMultiAttributionVal && !hasMultiAttributionProp && hasOnlyEventMetrics(sections.show.clauses)
          ? [BEHAVIOR_MULTI_ATTRIBUTION.value]
          : []),
      ],
      plotStyle: displayOptions.plotStyle,
      queryTypeOptions: getQueryTypeOptions(this.canUseCohorts()),
      rowTypes: getQueryModeRowTypes(this.state.queryMode, this.canUseCohorts()),
      selectedQueryType: this.state.queryMode,
      headerTitle: getQueryModeHeaderTitle(this.state.queryMode, this.canUseCohorts()),
      showEntryRename: true,
      showSpecialDateProperty: true,
      showSpecialDatasetProperty: this.params.project.isRollupProject(),
      /**
       * Hide Top Events from events-menu if table chart has another metric already. Insights table UI for
       * column/metric labeling looks weird when Top Events is mixed with other events
       */
      restrictTopEventsOnly: this.currentReport.displayOptions.chartType === ChartType.Table,
      supportB2bCohorts: this.canUseDataGroups(),
    };
  }

  get report() {
    return this.state.reports[this.state.queryMode];
  }

  updateReport(value) {
    this.state.reports[this.state.queryMode] = value;
    this.update();
  }

  onBookmarkUpdated(newBookmarkParams: InsightsBookmarkParams) {
    this.updateReports(newBookmarkParams);
  }

  // a bunch of stuff horked from insights-app/insights-query/index

  private updateReports(bookmarkParams: InsightsBookmarkParams) {
    const report = Report.fromBookmarkData(bookmarkParams);

    const {reports} = this.state;
    const dataGroupId = report.sections.getDataGroupId();

    let loadedReportMode;
    if (report?.isEmpty()) {
      loadedReportMode = this.state.queryMode === QueryMode.Groups ? QueryMode.Groups : QueryMode.Events;
    } else {
      loadedReportMode = report.sections.show.isPeopleOnlyQuery() ? QueryMode.Groups : QueryMode.Events;
    }

    const updatedReports = {
      [QueryMode.Events]: reports[QueryMode.Events]
        ? reports[QueryMode.Events].clone()
        : this.defaultReportState(false, dataGroupId),
      [QueryMode.Groups]: reports[QueryMode.Groups]
        ? reports[QueryMode.Groups].clone()
        : this.defaultReportState(true, dataGroupId),
      ...{
        [loadedReportMode]: report.clone(),
      },
    };

    // sync data group id to the other report
    const otherQueryMode = loadedReportMode === QueryMode.Events ? QueryMode.Groups : QueryMode.Events;
    const otherReport = updatedReports[otherQueryMode];
    if (dataGroupId !== otherReport.sections.getDataGroupId()) {
      const sanitizedOtherReport = sanitizeReportForDataGroup({
        report: otherReport,
        dataGroupId,
        dataGroups: this.params.dataGroups,
        customEvents: this.params.customEvents,
      });
      updatedReports[otherQueryMode] = sanitizedOtherReport;
    }

    this.update({reports: updatedReports, queryMode: loadedReportMode});
  }

  private defaultReportState(usePeopleMode = false, dataGroupId = null) {
    const showLastXDaysFlag = SHOW_LAST_X_DAYS_FEATURE_FLAGS.filter((flag: ProjectFeatureFlag) =>
      this.params.project?.hasFeatureFlag(flag),
    );

    return Report.createDefaultReport({
      dataGroupId,
      defaultQueryPeople: usePeopleMode || this.shouldDefaultToPeopleMode(),
      showLastXDays: showLastXDaysFlag ? showLastXDaysFlag[0] : null,
    });
  }

  private shouldDefaultToPeopleMode() {
    return !!this.params.project && this.params.project.isPeopleOnlyProject();
  }

  canUseBehaviors() {
    return hasInlineBehaviors(this.params.project);
  }
}

export const InsightsQueryBuilderEl = defineAndMakeJsxFactory(`insights-query-builder`, InsightsQueryBuilder);
export const InsightsQueryBuilderReact = panel2react(`insights-query-builder`, InsightsQueryBuilder);
